import {
  CssCustomPropertyGroupStyleItem,
  CssPropertyType,
  StylesGroup,
  StyleValues,
} from '../types';
import { extractShadowOpacity } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/shadow/opacityParser';
import { decomposeProperties } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { CSS_PROPERTIES } from '@wix/component-protocol/schema';
import { getAllowedPropertiesForAction } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/constants';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';
import { isAllowedStylePresent } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/actionValidators/actionVisibilityRules';
import { buildRootContextInfosMap } from '../common/grouping/helpers/buildRootContextInfosMap';
import { VirtualManifestAPI } from '@wix/editor-platform-manifest-services';
import { getPossibleCssProperties } from '../common/grouping/propertyRelationships';

/**
 * Shadow group display filter
 *
 * Filters component groups in the shadow panel based on shadow opacity values.
 *
 * Logic for each root component and its children:
 * 1. If ANY group (root or child) has visible shadow (opacity > 0):
 *    - Show only groups that have visible shadow (filters both roots and children)
 * 2. If NO group has visible shadow:
 *    - Show only root groups that are valid for shadow properties (have manifest support)
 *    - Hide child groups
 */
export const shadowGroupDisplayFilter = (
  groups: StylesGroup[],
  rootContexts: VirtualManifestAPI[],
): StylesGroup[] => {
  if (groups.length === 0) {
    return groups;
  }

  const result: StylesGroup[] = [];
  const rootContextInfos = buildRootContextInfosMap(groups, rootContexts);

  for (const rootContextInfo of Object.values(rootContextInfos)) {
    const contextGroups = [
      ...rootContextInfo.rootGroups,
      ...rootContextInfo.childGroups,
    ];
    const isSomeShadowVisible = contextGroups.some(hasVisibleShadow);

    if (isSomeShadowVisible) {
      result.push(...contextGroups.filter(hasVisibleShadow));
      continue;
    }

    for (const rootGroup of rootContextInfo.rootGroups) {
      if (
        isAllowedStylePresent(
          rootGroup.context,
          new Set(
            getAllowedPropertiesForAction(
              StylesDerivedNativeActions.shadowStyles,
            ),
          ),
          rootGroup.styleNamespace,
        )
      ) {
        result.push(rootGroup);
      }
    }
  }

  return result;
};

/**
 * Checks if a group has any visible shadow (opacity > 0)
 * Routes to the appropriate check function based on the group's styleNamespace
 */
const hasVisibleShadow = (group: StylesGroup): boolean => {
  const context = group.context;
  const styles = context.query.getStyles();
  if (group.styleNamespace === 'cssProperties') {
    return hasVisibleShadowInCssProperties(
      styles,
      getPossibleCssProperties(group.items.map((item) => item.styleItemId)),
    );
  }

  return hasVisibleShadowInCssCustomProperties(group.items, styles);
};

/**
 * Checks if a group has any visible shadow in CSS properties namespace
 */
const hasVisibleShadowInCssProperties = (
  styles: StyleValues,
  possibleCssProperties: Set<string>,
): boolean => {
  // Get allowed property types for shadow action
  const allowedPropertyTypes = new Set<CssPropertyType>(
    getAllowedPropertiesForAction(StylesDerivedNativeActions.shadowStyles),
  );

  if (styles?.cssProperties) {
    const relevantCssProps: Record<string, string> = Object.fromEntries(
      Object.entries(styles.cssProperties).filter(
        ([propType]) =>
          possibleCssProperties.has(propType) &&
          allowedPropertyTypes.has(propType as any),
      ),
    );

    if (Object.keys(relevantCssProps).length > 0) {
      const decomposed = decomposeProperties(relevantCssProps);

      if (decomposed[CSS_PROPERTIES.CSS_PROPERTY_TYPE.boxShadow]) {
        const opacity = extractShadowOpacity(
          decomposed[CSS_PROPERTIES.CSS_PROPERTY_TYPE.boxShadow],
        );
        if (opacity > 0) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Checks if a group has any visible shadow in CSS custom properties namespace
 */
const hasVisibleShadowInCssCustomProperties = (
  items: CssCustomPropertyGroupStyleItem[],
  styles: StyleValues,
): boolean => {
  // Get allowed property types for shadow action
  const allowedPropertyTypes = new Set<CssPropertyType>(
    getAllowedPropertiesForAction(StylesDerivedNativeActions.shadowStyles),
  );

  if (styles?.cssCustomProperties) {
    const customPropsToDecompose: Record<string, string> = {};
    for (const item of items) {
      const cssPropertyType = item.styleItem.cssPropertyType;
      if (cssPropertyType && allowedPropertyTypes.has(cssPropertyType)) {
        customPropsToDecompose[cssPropertyType] =
          styles.cssCustomProperties[item.styleItemId];
      }
    }

    if (Object.keys(customPropsToDecompose).length > 0) {
      const decomposed = decomposeProperties(customPropsToDecompose);

      if (decomposed[CSS_PROPERTIES.CSS_PROPERTY_TYPE.boxShadow]) {
        const opacity = extractShadowOpacity(
          decomposed[CSS_PROPERTIES.CSS_PROPERTY_TYPE.boxShadow],
        );
        if (opacity > 0) {
          return true;
        }
      }
    }
  }

  return false;
};
